import { Component, OnDestroy, OnInit, Input, NgZone, ChangeDetectionStrategy, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subscription, BehaviorSubject, Subject, fromEvent, merge } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { FreshchatService } from '@shared/services/freshchat.service';
import { DashboardScope, DashboardService } from './dashboard.service';

export type DashboardViewerLayout = 'page' | 'fill';

interface IframeMessage {
  type: 'HEIGHT_UPDATE' | 'NAVIGATION' | 'REQUEST_TOKEN' | 'CHATBOT_PANEL_STATE';
  height?: number;
  dashboardId?: string;
  url?: string;
  chatPanelOpen?: boolean;
}

@Component({
  selector: 'oes-dashboard-viewer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dashboard-viewer-container">
      <div *ngIf="errorMessage$ | async as errorMessage; else showIframe" class="error-message">
        {{ errorMessage }}
      </div>
      <ng-template #showIframe>
        <iframe
          #dashboardIframe
          *ngIf="iframeSrc$ | async as iframeSrc"
          [src]="iframeSrc"
          class="dashboard-iframe"
          title="Dashboard"
          loading="eager"
        ></iframe>
      </ng-template>
    </div>
  `,
  styleUrls: ['./dashboard-viewer.component.scss'],
})
export class DashboardViewerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) orgId!: string;
  @Input() programId?: string;
  @Input() layout: DashboardViewerLayout = 'page';
  @ViewChild('dashboardIframe') private iframeElement?: ElementRef<HTMLIFrameElement>;

  @HostBinding('class.dashboard-viewer--fill')
  get isFillLayout(): boolean {
    return this.layout === 'fill';
  }

  private iframeSrcSubject = new BehaviorSubject<SafeResourceUrl | null>(null);
  private iframeHeightSubject = new BehaviorSubject<number | null>(null);
  private errorMessageSubject = new BehaviorSubject<string | null>(null);

  iframeSrc$ = this.iframeSrcSubject.asObservable();
  iframeHeight$ = this.iframeHeightSubject.asObservable();
  errorMessage$ = this.errorMessageSubject.asObservable();

  private currentDashboardId: string | null = null;
  private destroy$ = new Subject<void>();
  private oauthSubscription: Subscription | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private dashboardService: DashboardService,
    private ngZone: NgZone,
    private oauthService: OAuthService,
    private freshchatService: FreshchatService,
  ) { }

  ngOnInit() {
    this.setupMessageHandling();
    this.setupTokenRefreshHandler();
    this.initializeDashboard();
  }

  private initializeDashboard() {
    this.loadDashboardForOrg(this.orgId);
  }

  private get scope(): DashboardScope {
    return this.programId ? { location: 'program', programId: this.programId } : { location: 'home' };
  }

  ngOnDestroy() {
    // Leaving the dashboard view must never strand a hidden Freshchat widget.
    this.freshchatService.show();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.oauthSubscription) {
      this.oauthSubscription.unsubscribe();
    }
  }

  private loadDashboardForOrg(orgId: string) {
    this.iframeHeightSubject.next(null);
    this.errorMessageSubject.next(null);

    this.dashboardService
      .getInitialDashboardId(orgId, this.scope)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dashboardId) => {
          if (dashboardId) {
            this.loadDashboard(dashboardId);
          } else {
            this.errorMessageSubject.next('Unable to load dashboard. Please try again later.');
          }
        },
        error: () => {
          this.errorMessageSubject.next('Unable to load dashboard. Please try again later.');
        },
      });
  }

  private loadDashboard(dashboardId: string) {
    const baseUrl = environment.dashboardBuilderUrl;
    const embedUrl = `${baseUrl}/dashboards/${dashboardId}/embed`;
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    this.iframeSrcSubject.next(safeUrl);
    this.currentDashboardId = dashboardId;
  }

  private setupMessageHandling() {
    const messages$ = fromEvent<MessageEvent>(window, 'message').pipe(
      filter((event) => event.origin === environment.dashboardBuilderUrl),
      map((event) => event.data as IframeMessage),
      takeUntil(this.destroy$),
    );

    messages$.pipe(filter((msg) => msg.type === 'HEIGHT_UPDATE' && msg.height !== undefined)).subscribe((msg) => {
      this.ngZone.run(() => {
        this.iframeHeightSubject.next(msg.height!);
      });
    });

    messages$.pipe(filter((msg) => msg.type === 'NAVIGATION' && msg.dashboardId !== undefined)).subscribe((msg) => {
      this.ngZone.run(() => {
        if (msg.dashboardId !== this.currentDashboardId) {
          this.iframeHeightSubject.next(null);
        }
      });
    });

    messages$.pipe(filter((msg) => msg.type === 'REQUEST_TOKEN')).subscribe(() => {
      this.ngZone.run(() => {
        this.sendTokenToIframe();
      });
    });

    // The chat panel overlaps the Freshchat widget, so hide it while the panel is open.
    messages$.pipe(filter((msg) => msg.type === 'CHATBOT_PANEL_STATE')).subscribe((msg) => {
      this.ngZone.run(() => {
        if (msg.chatPanelOpen) {
          this.freshchatService.hide();
        } else {
          this.freshchatService.show();
        }
      });
    });
  }

  private setupTokenRefreshHandler() {
    this.oauthSubscription = this.oauthService.events.subscribe((event) => {
      if (event.type === 'token_refreshed') {
        this.sendTokenToIframe();
      }
    });
  }


  private sendTokenToIframe() {
    const token = this.oauthService.getAccessToken();

    if (!token) {
      console.warn('No auth token available to send to iframe');
      return;
    }

    const iframe = this.iframeElement?.nativeElement;

    if (!iframe?.contentWindow) {
      console.error('Cannot send token: iframe not found');
      return;
    }

    try {
      iframe.contentWindow.postMessage(
        {
          type: 'AUTH_TOKEN',
          token: token,
        },
        environment.dashboardBuilderUrl,
      );
    } catch (error) {
      console.error('Error sending token to iframe:', error);
    }
  }
}
